/**
 * Button Component
 * 
 * Primary, secondary, and ghost variants.
 * Meets 44px minimum touch target.
 */

import { ButtonHTMLAttributes, forwardRef, ReactNode } from 'react';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost';
  size?: 'sm' | 'md' | 'lg';
  isLoading?: boolean;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  fullWidth?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      isLoading = false,
      leftIcon,
      rightIcon,
      fullWidth = false,
      children,
      disabled,
      className = '',
      ...props
    },
    ref
  ) => {
    const baseStyles = `
      inline-flex items-center justify-center gap-2
      font-semibold rounded-lg
      transition-all duration-250
      focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2
      disabled:cursor-not-allowed
    `;
    
    // Premium: Primary is charcoal-800 (dark treatment), teal only for outline variant
    const variants = {
      primary: `
        bg-charcoal-800 text-white
        hover:bg-charcoal-700
        active:bg-charcoal-900 active:scale-[0.98]
        disabled:bg-charcoal-300 disabled:shadow-none
        focus-visible:ring-charcoal-500
      `,
      secondary: `
        bg-transparent text-teal-500
        border-2 border-teal-500
        hover:bg-teal-50 hover:border-teal-600
        active:bg-teal-100
        disabled:border-charcoal-300 disabled:text-charcoal-300
        focus-visible:ring-teal-500
      `,
      ghost: `
        bg-transparent text-charcoal-600
        hover:bg-charcoal-100
        active:bg-charcoal-200
        disabled:text-charcoal-300
        focus-visible:ring-charcoal-400
      `,
    };
    
    const sizes = {
      sm: 'min-h-[36px] px-3 py-1.5 text-sm',
      md: 'min-h-touch px-5 py-2.5 text-base',
      lg: 'min-h-[52px] px-6 py-3 text-lg',
    };
    
    return (
      <button
        ref={ref}
        disabled={disabled || isLoading}
        className={`
          ${baseStyles}
          ${variants[variant]}
          ${sizes[size]}
          ${fullWidth ? 'w-full' : ''}
          ${className}
        `}
        {...props}
      >
        {isLoading ? (
          <>
            <Spinner />
            <span>Loading...</span>
          </>
        ) : (
          <>
            {leftIcon && <span className="flex-shrink-0">{leftIcon}</span>}
            {children}
            {rightIcon && <span className="flex-shrink-0">{rightIcon}</span>}
          </>
        )}
      </button>
    );
  }
);

Button.displayName = 'Button';

// Loading spinner
function Spinner() {
  return (
    <svg
      className="animate-spin h-5 w-5"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );
}

export default Button;
